/**
 * VirtualList Component
 *
 * React Native FlashList benzeri basit API ile virtual scrolling
 * @tanstack/react-virtual kullanarak optimize edilmiş liste rendering
 *
 * Features:
 * - ✅ Virtual scrolling (sadece görünür item'lar render)
 * - ✅ Dinamik yükseklik desteği
 * - ✅ Infinite scroll (TanStack Query entegrasyonu)
 * - ✅ Grid ve List layout
 * - ✅ Reversed layout (Chat gibi)
 * - ✅ Empty, Loading, Footer states
 * - ✅ IntersectionObserver ile otomatik load more
 *
 * Usage:
 * ```tsx
 * <VirtualList
 *   data={items}
 *   renderItem={(item, index) => <ItemComponent item={item} />}
 *   estimatedItemSize={200}
 *   onEndReached={fetchNextPage}
 *   infiniteQuery={{ hasNextPage, isFetchingNextPage, isLoading }}
 * />
 * ```
 */

import React, { useRef, useEffect, useMemo } from "react";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

// ===== Types =====

export interface InfiniteQueryState {
  /** TanStack Query'den: Sonraki sayfa var mı? */
  hasNextPage?: boolean;
  /** TanStack Query'den: Sonraki sayfa yükleniyor mu? */
  isFetchingNextPage?: boolean;
  /** TanStack Query'den: İlk yükleme mi? */
  isLoading?: boolean;
  /** TanStack Query'den: Hata var mı? */
  error?: Error | null;
}

export interface VirtualListProps<T> {
  /** Render edilecek data array */
  data: T[];

  /** Item render fonksiyonu - FlashList benzeri */
  renderItem: (item: T, index: number) => React.ReactNode;

  /** Her item için unique key (optional, default: index) */
  keyExtractor?: (item: T, index: number) => string;

  /** Tahmini item yüksekliği (performance için) */
  estimatedItemSize?: number;

  /** Infinite scroll: Sayfa sonu trigger fonksiyonu */
  onEndReached?: () => void;

  /** Infinite scroll: Trigger threshold (0-1 arası, default: 0.85) */
  onEndReachedThreshold?: number;

  /** Infinite scroll (top): Listenin başına yaklaşıldığında tetiklenir (chat gibi) */
  onStartReached?: () => void;

  /** Infinite scroll (top): Trigger threshold (0-1 arası, default: 0.85) */
  onStartReachedThreshold?: number;

  /** TanStack Query infinite state */
  infiniteQuery?: InfiniteQueryState;

  /** Empty state component */
  ListEmptyComponent?: React.ReactNode;

  /** Header component (list başında) */
  ListHeaderComponent?: React.ReactNode;

  /** Footer component (list sonunda) */
  ListFooterComponent?: React.ReactNode;

  /** Loading component (custom) */
  LoadingComponent?: React.ReactNode;

  /** Loading more component (custom) */
  LoadingMoreComponent?: React.ReactNode;

  /** End of list component (custom) */
  EndOfListComponent?: React.ReactNode;

  /** Layout: "list" | "grid" */
  layout?: "list" | "grid";

  /** Grid columns (FlashList benzeri - layout="grid" ise) */
  numColumns?: number;

  /** Minimum column width (responsive grid için) - örn: "200px" */
  minColumnWidth?: string;

  /** Grid gap (layout="grid" ise) */
  gridGap?: string;

  /** Reversed layout (Chat gibi, en yeni altta) */
  reversed?: boolean;

  /** Dışarıdan scroll container ref’i vermek istersen (chat scroll kontrolü vb.) */
  scrollContainerRef?: React.RefObject<HTMLDivElement | null>;

  /**
   * Prepend (listenin başına) yeni item’lar eklendiğinde scroll pozisyonunu koru.
   * Chat'te “load older messages” sonrası viewport zıplamasını önler.
   */
  maintainScrollPositionOnPrepend?: boolean;

  /** Container className */
  className?: string;

  /** Container style */
  style?: React.CSSProperties;

  /** Scroll event handler (chat scroll logic gibi) */
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;

  /** Overscan count (kaç tane extra item render edilsin) */
  overscan?: number;

  /** Debug mode (console.log ile bilgi göster) */
  debug?: boolean;
}

// ===== Component =====

export function VirtualList<T = any>({
  data,
  renderItem,
  keyExtractor,
  estimatedItemSize = 200,
  onEndReached,
  onEndReachedThreshold = 0.85,
  onStartReached,
  onStartReachedThreshold = 0.85,
  infiniteQuery,
  ListEmptyComponent,
  ListHeaderComponent,
  ListFooterComponent,
  LoadingComponent,
  LoadingMoreComponent,
  EndOfListComponent,
  layout = "list",
  numColumns = 1,
  minColumnWidth = "200px",
  gridGap = "1rem",
  reversed = false,
  scrollContainerRef,
  maintainScrollPositionOnPrepend = false,
  className,
  style,
  onScroll,
  overscan = 5,
  debug = false,
}: VirtualListProps<T>) {
  // Refs
  const internalParentRef = useRef<HTMLDivElement>(null);
  const parentRef = scrollContainerRef ?? internalParentRef;
  const endSentinelRef = useRef<HTMLDivElement>(null);
  const startSentinelRef = useRef<HTMLDivElement>(null);
  const lastTriggerTimeRef = useRef<number>(0);
  const lastStartTriggerTimeRef = useRef<number>(0);

  // Prepend scroll stabilization
  const prevFirstKeyRef = useRef<string | null>(null);
  const anchorKeyRef = useRef<string | null>(null);
  const anchorOffsetRef = useRef<number>(0);
  const pendingRestoreRef = useRef<boolean>(false);

  // Extract query states
  const {
    hasNextPage = false,
    isFetchingNextPage = false,
    isLoading = false,
    error = null,
  } = infiniteQuery || {};

  // Debug logger
  const log = (...args: any[]) => {
    if (debug) {
      console.log("[VirtualList]", ...args);
    }
  };

  // ===== Calculate columns for grid layout =====
  const columnsPerRow = useMemo(() => {
    if (layout !== "grid") return 1;
    if (numColumns > 1) return numColumns;
    if (numColumns === 0 && parentRef.current) {
      // Calculate responsive columns based on minColumnWidth
      const containerWidth = parentRef.current.clientWidth;
      const gapValue = parseFloat(gridGap) || 16;
      const minWidth = parseFloat(minColumnWidth) || 200;
      const availableWidth = containerWidth;
      const columns = Math.floor((availableWidth + gapValue) / (minWidth + gapValue));
      return Math.max(1, columns);
    }
    return 1;
  }, [layout, numColumns, minColumnWidth, gridGap]);

  // ===== Virtualizer Setup =====
  // Grid layout için row count hesapla
  const rowCount = layout === "grid" ? Math.ceil(data.length / columnsPerRow) : data.length;
  
  const virtualizer = useVirtualizer({
    count: layout === "grid" ? rowCount : data.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimatedItemSize,
    overscan,
    // Reversed scroll için
    ...(reversed && {
      initialOffset: 0,
      scrollMargin: 0,
    }),
  });

  // Virtual items
  const virtualItems = virtualizer.getVirtualItems();

  // ===== Infinite Scroll (end) with IntersectionObserver =====
  useEffect(() => {
    if (!onEndReached || !hasNextPage || isFetchingNextPage || isLoading) {
      return;
    }

    const sentinel = endSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const now = Date.now();
          const timeSinceLastTrigger = now - lastTriggerTimeRef.current;

          // Debounce: 500ms içinde tekrar trigger yapma
          if (timeSinceLastTrigger > 500) {
            lastTriggerTimeRef.current = now;
            log("Infinite scroll triggered");
            onEndReached();
          }
        }
      },
      {
        root: parentRef.current,
        threshold: onEndReachedThreshold,
        rootMargin: "200px", // 200px önceden trigger
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    onEndReached,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    onEndReachedThreshold,
    log,
  ]);

  // ===== Infinite Scroll (start/top) with IntersectionObserver =====
  useEffect(() => {
    if (!onStartReached || !hasNextPage || isFetchingNextPage || isLoading) {
      return;
    }

    const sentinel = startSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          const now = Date.now();
          const timeSinceLastTrigger = now - lastStartTriggerTimeRef.current;

          // Debounce: 500ms içinde tekrar trigger yapma
          if (timeSinceLastTrigger > 500) {
            lastStartTriggerTimeRef.current = now;

            // Capture anchor before prepend
            if (maintainScrollPositionOnPrepend) {
              const items = virtualizer.getVirtualItems();
              const first = items[0];
              const container = parentRef.current;
              if (first && container) {
                const item = data[first.index];
                const key = keyExtractor
                  ? keyExtractor(item, first.index)
                  : String(first.index);
                anchorKeyRef.current = key;
                anchorOffsetRef.current = container.scrollTop - first.start;
                pendingRestoreRef.current = true;
              }
            }

            log("Start infinite scroll triggered");
            onStartReached();
          }
        }
      },
      {
        root: parentRef.current,
        threshold: onStartReachedThreshold,
        rootMargin: "200px",
      }
    );

    observer.observe(sentinel);

    return () => {
      observer.disconnect();
    };
  }, [
    onStartReached,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    onStartReachedThreshold,
    maintainScrollPositionOnPrepend,
    keyExtractor,
    data,
    virtualizer,
    log,
  ]);

  // ===== Restore scroll position after prepend =====
  useEffect(() => {
    if (!maintainScrollPositionOnPrepend) return;
    if (!pendingRestoreRef.current) return;
    const container = parentRef.current;
    const anchorKey = anchorKeyRef.current;
    if (!container || !anchorKey) return;

    // Only restore once fetching is done (data should be updated)
    if (isFetchingNextPage) return;

    const findIndex = () => {
      if (!keyExtractor) return null;
      // O(n) lookup - acceptable for chat pages
      for (let i = 0; i < data.length; i++) {
        const k = keyExtractor(data[i], i);
        if (k === anchorKey) return i;
      }
      return null;
    };

    const idx = findIndex();
    if (idx === null) {
      pendingRestoreRef.current = false;
      return;
    }

    // Let virtualizer measure/layout settle before restoring
    requestAnimationFrame(() => {
      const start = (
        virtualizer.getOffsetForIndex(idx, "start") as [number, "start"]
      )[0];
      container.scrollTop = start + anchorOffsetRef.current;
      pendingRestoreRef.current = false;
    });
  }, [
    data,
    keyExtractor,
    maintainScrollPositionOnPrepend,
    isFetchingNextPage,
    virtualizer,
  ]);

  // Track first key (debug/heuristic)
  useEffect(() => {
    if (!maintainScrollPositionOnPrepend) return;
    if (data.length === 0) return;
    const firstKey = keyExtractor ? keyExtractor(data[0], 0) : "0";
    prevFirstKeyRef.current = firstKey;
  }, [data, keyExtractor, maintainScrollPositionOnPrepend]);

  // ===== Grid Layout Helper =====
  const gridStyle = useMemo(() => {
    if (layout === "grid") {
      // Auto-responsive grid with minColumnWidth
      // numColumns === 0 ise responsive auto-fill kullan (minColumnWidth ile)
      // numColumns > 1 ise fixed columns kullan
      // numColumns === 1 ise single column
      const gridTemplateColumns =
        numColumns === 0
          ? `repeat(auto-fill, minmax(${minColumnWidth}, 1fr))`
          : numColumns > 1
          ? `repeat(${numColumns}, 1fr)`
          : "1fr";

      return {
        display: "grid",
        gridTemplateColumns,
        gridAutoRows: "auto", // Auto height rows (content-based)
        gap: gridGap,
      };
    }
    return undefined;
  }, [layout, numColumns, minColumnWidth, gridGap]);

  // ===== Loading State =====
  if (isLoading && data.length === 0) {
    if (LoadingComponent) {
      return <>{LoadingComponent}</>;
    }

    return (
      <div className="flex flex-col h-full items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400 dark:text-gray-500 mb-3" />
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Yükleniyor...
        </p>
      </div>
    );
  }

  // ===== Empty State =====
  if (!isLoading && data.length === 0) {
    if (ListEmptyComponent) {
      return <>{ListEmptyComponent}</>;
    }

    return (
      <div className="flex flex-col h-full items-center justify-center p-8">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Henüz içerik yok
        </p>
      </div>
    );
  }

  // ===== Error State =====
  if (error) {
    return (
      <div className="flex flex-col h-full items-center justify-center p-8">
        <p className="text-sm text-red-600 dark:text-red-400 mb-2">
          Bir hata oluştu
        </p>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          {error.message}
        </p>
      </div>
    );
  }

  // ===== Main Render =====
  return (
    <div
      ref={parentRef}
      className={cn(
        "h-full overflow-y-auto",
        reversed && "flex flex-col-reverse",
        className
      )}
      style={style}
      onScroll={onScroll}
    >
      {/* Start Sentinel (top) */}
      {hasNextPage && !isFetchingNextPage && (
        <div
          ref={startSentinelRef}
          className="h-1 w-full flex-shrink-0"
          aria-hidden="true"
        />
      )}

      {/* Header */}
      {ListHeaderComponent && (
        <div className="flex-shrink-0">{ListHeaderComponent}</div>
      )}

      {/* Virtual Items Container */}
      <div
        style={{
          height: layout === "grid" ? "auto" : `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
          ...gridStyle,
        }}
      >
        {layout === "grid" ? (
          // Grid layout: Tüm item'ları render et (CSS Grid handles positioning)
          data.map((item, index) => {
            const key = keyExtractor
              ? keyExtractor(item, index)
              : String(index);
            return (
              <div key={key} data-index={index}>
                {renderItem(item, index)}
              </div>
            );
          })
        ) : (
          // List layout: Virtual items kullan
          virtualItems.map((virtualItem: VirtualItem) => {
            const item = data[virtualItem.index];
            const key = keyExtractor
              ? keyExtractor(item, virtualItem.index)
              : String(virtualItem.index);

            return (
              <div
                key={key}
                data-index={virtualItem.index}
                ref={virtualizer.measureElement}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  transform: `translateY(${virtualItem.start}px)`,
                }}
              >
                {renderItem(item, virtualItem.index)}
              </div>
            );
          })
        )}
      </div>

      {/* Loading More Indicator */}
      {isFetchingNextPage && (
        <div className="flex-shrink-0 flex justify-center items-center py-6">
          {LoadingMoreComponent || (
            <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Yükleniyor...</span>
            </div>
          )}
        </div>
      )}

      {/* End of List Indicator */}
      {!hasNextPage && data.length > 0 && (
        <div className="flex-shrink-0 flex justify-center items-center py-4">
          {EndOfListComponent || (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tüm içerik gösterildi
            </p>
          )}
        </div>
      )}

      {/* Infinite Scroll Sentinel */}
      {hasNextPage && !isFetchingNextPage && (
        <div
          ref={endSentinelRef}
          className="h-1 w-full flex-shrink-0"
          aria-hidden="true"
        />
      )}

      {/* Footer */}
      {ListFooterComponent && (
        <div className="flex-shrink-0">{ListFooterComponent}</div>
      )}
    </div>
  );
}

// ===== Export =====
export default VirtualList;
